import { SendOutlined } from '@ant-design/icons';
import { Button, Input, theme } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTimeAgo } from '../../library/utils/intl.js';

const isTeamMessage = (item) => item.source === 1

const CommunicationPanel = ({ messages, placeholder = "Send message to Leoprinting", onSubmit }) => {
    const { token } = theme.useToken()
    const [inputMessage, setInputMessage] = useState('')
    const listRef = useRef(null)

    const orderedMessages = useMemo(() => {
        const list = Array.isArray(messages) ? [...messages] : []
        return list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    }, [messages])

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight
        }
    }, [orderedMessages])

    const handleSend = () => {
        const comment = inputMessage.trim()
        if (comment?.length > 0) {
            onSubmit(comment)
            setInputMessage('')
        }
    }

    const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key.toLowerCase() === 'j') {
            e.preventDefault()
            handleSend()
        }
    }

    return (
        <>
            <div
                ref={listRef}
                style={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    padding: '12px',
                    marginBottom: '0.5rem',
                    background: token.colorBgLayout,
                    borderRadius: token.borderRadiusLG,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.8rem',
                }}
            >
                {orderedMessages.length === 0 && (
                    <div style={{ textAlign: 'center', color: token.colorTextSecondary, padding: '1.5rem 0' }}>
                        No messages yet
                    </div>
                )}
                {orderedMessages.map((item) => {
                    const isTeam = isTeamMessage(item)
                    const name = item.user?.name ?? item.sender_name ?? 'Leoprinting'
                    return (
                        <div key={item.id} style={{ display: 'flex', justifyContent: isTeam ? 'flex-start' : 'flex-end' }}>
                            <div style={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: isTeam ? 'flex-start' : 'flex-end' }}>
                                <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 4, paddingInline: 4 }}>
                                    {name}
                                    <span style={{ marginLeft: 8, opacity: 0.8 }}>{getTimeAgo(item.createdAt)}</span>
                                </div>
                                <div
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: token.borderRadius,
                                        borderTopLeftRadius: isTeam ? 2 : token.borderRadius,
                                        borderTopRightRadius: isTeam ? token.borderRadius : 2,
                                        background: isTeam ? token.colorFillTertiary : token.colorSuccess,
                                        color: isTeam ? token.colorText : '#ffffff',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                    }}
                                >
                                    {item.message}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="chat-composer" style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', background: token.colorFillTertiary, borderRadius: token.borderRadiusLG, padding: '12px' }}>
                <Input.TextArea
                    name="comment"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    variant="borderless"
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    style={{ background: 'transparent', padding: '4px 0', flex: 1 }}
                />
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    iconPosition="end"
                    disabled={inputMessage.trim().length === 0}
                    onClick={handleSend}
                >Send</Button>
            </div>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: '0.25rem' }}>
                Press Ctrl+J to send
            </div>
        </>
    )
}
CommunicationPanel.propTypes = {
    messages: PropTypes.arrayOf(PropTypes.shape({
        message: PropTypes.string.isRequired,
        user: PropTypes.object,
        sender_name: PropTypes.string,
        source: PropTypes.number.isRequired,
    })),
    onSubmit: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
}

export { CommunicationPanel };
